import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, Trash2, X, ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import { useConfirm } from './ConfirmDialog';
import { useToast } from '../ToastProvider';
import { anhangArt, anzeigeTyp } from '../../utils/anhangArt';
import { oeffnePdf } from '../../utils/pdfAnzeige';
import { einpassMassstab } from '../../utils/pdfEinpassen';
import './AnhangViewer.css';

// #636: EIN Betrachter für Bilder und PDFs, am Arbeitsplatz und mobil.
//   - PDF: alle Seiten untereinander (vertikal blättern), zoomen
//   - Bild: zoomen
//   - zwischen den Anhängen: Pfeile < >, Pfeiltasten, horizontales Wischen
// Anhänge: [{ id, name, typ, laden: () => Promise<Blob>, downloadUrl? }].
// Geladen wird nur der aktuelle Anhang. Zoom 1 heißt "eingepasst":
// beim PDF die ganze Seite, Breite und Höhe (utils/pdfEinpassen).

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 5;
const ZOOM_SCHRITT = 0.5;
const WISCH_MIN_PX = 60;

const begrenze = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100));

function PdfSeiten({ dokument, zoom, breite, hoehe, seiteText }) {
  const [anzahl, setAnzahl] = useState(dokument?.numPages || 0);
  const canvasRefs = useRef([]);

  useEffect(() => { setAnzahl(dokument?.numPages || 0); }, [dokument]);

  useEffect(() => {
    if (!dokument || !anzahl) return undefined;
    let abgebrochen = false;
    const laufend = [];
    const zeichne = async () => {
      const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
      for (let nr = 1; nr <= anzahl && !abgebrochen; nr += 1) {
        const seite = await dokument.getPage(nr);
        if (abgebrochen) return;
        const basis = seite.getViewport({ scale: 1 });
        const massstab = einpassMassstab(basis, breite, hoehe) * zoom;
        const vp = seite.getViewport({ scale: massstab * dpr });
        const canvas = canvasRefs.current[nr - 1];
        if (!canvas) continue;
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        canvas.style.width = `${Math.floor(vp.width / dpr)}px`;
        canvas.style.height = `${Math.floor(vp.height / dpr)}px`;
        const ctx = canvas.getContext && canvas.getContext('2d');
        if (!ctx) continue;
        const auftrag = seite.render({ canvasContext: ctx, viewport: vp });
        laufend.push(auftrag);
        try { await auftrag.promise; } catch (_) {
          // Abgebrochen (neuer Zoom) oder Seite nicht darstellbar: die übrigen Seiten trotzdem zeichnen.
        }
      }
    };
    const timer = setTimeout(zeichne, 120);
    return () => {
      abgebrochen = true;
      clearTimeout(timer);
      laufend.forEach((a) => { try { a.cancel(); } catch (_) { /* schon fertig */ } });
    };
  }, [dokument, anzahl, zoom, breite, hoehe]);

  return (
    <div className="anhang-viewer-pdf">
      {Array.from({ length: anzahl }, (_, i) => (
        <canvas key={i} ref={(el) => { canvasRefs.current[i] = el; }} className="anhang-viewer-seite"
          role="img" aria-label={seiteText(i + 1, anzahl)} />
      ))}
    </div>
  );
}

const AnhangViewer = ({ anhaenge, startIndex = 0, onClose, darfLoeschen = false, onLoeschen }) => {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  const liste = Array.isArray(anhaenge) ? anhaenge : [];
  const [index, setIndex] = useState(() => Math.max(0, Math.min(startIndex, liste.length - 1)));
  const [zoom, setZoom] = useState(1);
  const [inhalt, setInhalt] = useState({ status: 'laedt' });
  const [loescht, setLoescht] = useState(false);
  const [masse, setMasse] = useState({ breite: 0, hoehe: 0 });
  const flaecheRef = useRef(null);
  const schliessenRef = useRef(null);
  const touchRef = useRef(null);

  const aktuell = liste[index];
  const art = aktuell ? anhangArt(aktuell) : null;

  // Liste geschrumpft (gelöscht): Index nachziehen, leer = schließen.
  useEffect(() => {
    if (liste.length === 0) { onClose(); return; }
    if (index > liste.length - 1) setIndex(liste.length - 1);
  }, [liste.length, index, onClose]);

  useEffect(() => { if (schliessenRef.current) schliessenRef.current.focus(); }, []);

  // Größe der Fläche für das Einpassen der PDF-Seiten (abzüglich Polster von .anhang-viewer-pdf)
  useEffect(() => {
    const el = flaecheRef.current;
    const messen = () => setMasse({
      breite: el && el.clientWidth ? el.clientWidth - 16 : 0,
      hoehe: el && el.clientHeight ? el.clientHeight - 16 : 0
    });
    messen();
    if (typeof ResizeObserver === 'function' && el) {
      const ro = new ResizeObserver(messen);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', messen);
    return () => window.removeEventListener('resize', messen);
  }, []);

  // Aktuellen Anhang laden
  const aktuelleId = aktuell ? aktuell.id : null;
  useEffect(() => {
    if (!aktuell) return undefined;
    let aktiv = true;
    let url = null;
    let dokument = null;
    setZoom(1);
    if (!art) { setInhalt({ status: 'keineVorschau' }); return undefined; }
    setInhalt({ status: 'laedt' });
    (async () => {
      try {
        const roh = await aktuell.laden();
        const blob = new Blob([roh], { type: anzeigeTyp(aktuell) });
        if (art === 'bild') {
          url = URL.createObjectURL(blob);
          if (aktiv) setInhalt({ status: 'ok', url });
        } else {
          dokument = await oeffnePdf(blob);
          if (aktiv) setInhalt({ status: 'ok', dokument });
          else if (dokument.destroy) dokument.destroy();
        }
      } catch (_) {
        if (aktiv) setInhalt({ status: 'fehler' });
      }
    })();
    return () => {
      aktiv = false;
      if (url) { try { URL.revokeObjectURL(url); } catch (_) { /* bereits frei */ } }
      if (dokument && dokument.destroy) { try { dokument.destroy(); } catch (_) { /* bereits zerstört */ } }
    };
    // Neu laden nur bei anderem Anhang, nicht bei jedem neuen Listen-Array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktuelleId, art]);

  const geheZu = useCallback((i) => {
    if (i < 0 || i > liste.length - 1) return;
    setIndex(i);
  }, [liste.length]);

  const zoomen = useCallback((z) => setZoom(prev => begrenze(typeof z === 'function' ? z(prev) : z)), []);

  // Tastatur: Pfeile blättern, +/- zoomen, Escape schließt
  useEffect(() => {
    const taste = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); geheZu(index - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); geheZu(index + 1); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomen(z => z + ZOOM_SCHRITT); }
      else if (e.key === '-') { e.preventDefault(); zoomen(z => z - ZOOM_SCHRITT); }
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [index, geheZu, zoomen, onClose]);

  // Strg + Mausrad zoomt (Desktop), ohne die Seite zu zoomen
  useEffect(() => {
    const el = flaecheRef.current;
    if (!el) return undefined;
    const rad = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      zoomen(z => z + (e.deltaY < 0 ? ZOOM_SCHRITT : -ZOOM_SCHRITT));
    };
    el.addEventListener('wheel', rad, { passive: false });
    return () => el.removeEventListener('wheel', rad);
  }, [zoomen]);

  // Wischen (ein Finger, nur eingepasst) und Zwei-Finger-Zoom
  const abstand = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const beiTouchStart = (e) => {
    if (e.touches.length === 2) {
      touchRef.current = { pinch: abstand(e.touches[0], e.touches[1]), zoom };
    } else if (e.touches.length === 1) {
      touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  const beiTouchMove = (e) => {
    const s = touchRef.current;
    if (s && s.pinch && e.touches.length === 2) {
      zoomen(s.zoom * (abstand(e.touches[0], e.touches[1]) / s.pinch));
    }
  };
  const beiTouchEnd = (e) => {
    const s = touchRef.current;
    touchRef.current = null;
    if (!s || s.pinch || zoom > 1) return;
    const p = e.changedTouches && e.changedTouches[0];
    if (!p) return;
    const dx = p.clientX - s.x;
    const dy = p.clientY - s.y;
    if (Math.abs(dx) < WISCH_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    geheZu(dx < 0 ? index + 1 : index - 1);
  };

  const loeschen = async () => {
    if (!aktuell || loescht || !onLoeschen) return;
    const ok = await confirm({ message: t('ticketDetail.confirmDeleteAttachment', 'Diesen Anhang wirklich löschen?'), danger: true });
    if (!ok) return;
    setLoescht(true);
    try {
      await onLoeschen(aktuell);
    } catch (err) {
      pushToast({ message: err?.response?.data?.error || t('ticketDetail.deleteAttachmentError', 'Fehler beim Löschen des Anhangs'), type: 'error' });
    } finally {
      setLoescht(false);
    }
  };

  if (!aktuell) return null;

  const seiteText = (n, gesamt) => t('viewer.page', 'Seite {{n}} von {{total}}').replace('{{n}}', String(n)).replace('{{total}}', String(gesamt));
  const prozent = `${Math.round(zoom * 100)} %`;
  const zoombar = !!art && inhalt.status === 'ok';

  let flaeche;
  if (inhalt.status === 'laedt') {
    flaeche = <div className="loading-spinner" role="status" aria-label={t('common.loading', 'Wird geladen…')} />;
  } else if (inhalt.status === 'fehler') {
    flaeche = <p className="anhang-viewer-hinweis" role="alert">{t('viewer.loadError', 'Die Datei konnte nicht geladen werden.')}</p>;
  } else if (inhalt.status === 'keineVorschau') {
    flaeche = <p className="anhang-viewer-hinweis">{t('viewer.noPreview', 'Für diese Datei gibt es keine Vorschau.')}</p>;
  } else if (art === 'bild') {
    flaeche = (
      <img src={inhalt.url} alt={aktuell.name || ''}
        className={`anhang-viewer-bild${zoom > 1 ? ' gezoomt' : ''}`}
        style={zoom > 1 ? { width: `${zoom * 100}%` } : undefined}
        onDoubleClick={() => zoomen(z => (z > 1 ? 1 : 2))} />
    );
  } else {
    flaeche = <PdfSeiten dokument={inhalt.dokument} zoom={zoom} breite={masse.breite} hoehe={masse.hoehe} seiteText={seiteText} />;
  }

  return createPortal(
    <div className="anhang-viewer" role="dialog" aria-modal="true" aria-label={aktuell.name || t('viewer.title', 'Anhang')}>
      <header className="anhang-viewer-kopf">
        <button type="button" ref={schliessenRef} className="anhang-viewer-knopf" onClick={onClose}
          aria-label={t('common.close', 'Schließen')} title={t('common.close', 'Schließen')}>
          <X size={22} />
        </button>
        <div className="anhang-viewer-titel">
          <span className="anhang-viewer-name">{aktuell.name}</span>
          {liste.length > 1 && <span className="anhang-viewer-zaehler">{index + 1} / {liste.length}</span>}
        </div>
        {zoombar && (
          <div className="anhang-viewer-zoom">
            <button type="button" className="anhang-viewer-knopf" onClick={() => zoomen(z => z - ZOOM_SCHRITT)} disabled={zoom <= ZOOM_MIN}
              aria-label={t('viewer.zoomOut', 'Verkleinern')} title={t('viewer.zoomOut', 'Verkleinern')}>
              <ZoomOut size={20} />
            </button>
            <span className="anhang-viewer-prozent" aria-live="polite">{prozent}</span>
            <button type="button" className="anhang-viewer-knopf" onClick={() => zoomen(z => z + ZOOM_SCHRITT)} disabled={zoom >= ZOOM_MAX}
              aria-label={t('viewer.zoomIn', 'Vergrößern')} title={t('viewer.zoomIn', 'Vergrößern')}>
              <ZoomIn size={20} />
            </button>
            {zoom > 1 && (
              <button type="button" className="anhang-viewer-knopf" onClick={() => zoomen(1)}
                aria-label={t('viewer.zoomReset', 'Einpassen')} title={t('viewer.zoomReset', 'Einpassen')}>
                <Maximize size={20} />
              </button>
            )}
          </div>
        )}
        {aktuell.downloadUrl && (
          <a className="anhang-viewer-knopf" href={aktuell.downloadUrl} download={aktuell.name || true}
            aria-label={t('ticketDetail.download', 'Herunterladen')} title={t('ticketDetail.download', 'Herunterladen')}>
            <Download size={20} />
          </a>
        )}
        {darfLoeschen && onLoeschen && (
          <button type="button" className="anhang-viewer-knopf" onClick={loeschen} disabled={loescht}
            aria-label={t('viewer.delete', 'Anhang löschen')} title={t('viewer.delete', 'Anhang löschen')}>
            <Trash2 size={20} />
          </button>
        )}
      </header>

      <div className="anhang-viewer-flaeche" ref={flaecheRef} data-testid="anhang-viewer-flaeche"
        onTouchStart={beiTouchStart} onTouchMove={beiTouchMove} onTouchEnd={beiTouchEnd}>
        {flaeche}
      </div>

      {liste.length > 1 && (
        <>
          <button type="button" className="anhang-viewer-pfeil links" onClick={() => geheZu(index - 1)} disabled={index === 0}
            aria-label={t('viewer.prev', 'Vorheriger Anhang')} title={t('viewer.prev', 'Vorheriger Anhang')}>
            <ChevronLeft size={32} />
          </button>
          <button type="button" className="anhang-viewer-pfeil rechts" onClick={() => geheZu(index + 1)} disabled={index === liste.length - 1}
            aria-label={t('viewer.next', 'Nächster Anhang')} title={t('viewer.next', 'Nächster Anhang')}>
            <ChevronRight size={32} />
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

export default AnhangViewer;
